import { useEffect } from 'react';
import { Vector as VectorSource } from 'ol/source';
import { Vector as VectorLayer } from 'ol/layer';

import { useProject } from '@contexts/Project.context';
import { getLayerByCustomId, deleteLayerByCustomId } from '@utils/map/helpers';
import Checkbox from '../sidebars/sidebarElements/LayerCheckbox';

/**
 * Removes overlays with a specific element class name from a map object.
 *
 * @param {Object} options The options for removing overlays.
 * @param {Object} options.mapObject The map object from which to remove overlays.
 * @param {string} options.className The class name of the element of the overlay to be removed.
 * @returns {void}
 */
const removeOverlaysWithElementClassName = ({ mapObject, className }) => {
	mapObject
		.getOverlays()
		.getArray()
		.filter(overlay => overlay?.getElement()?.className?.includes(className))
		.forEach(overlay => mapObject.removeOverlay(overlay));
};

/**
 * MeasurementLayer component.
 *
 * @returns {JSX.Element} The MeasurementLayer component.
 */
const MeasurementLayer = ({ layerId = 'measurementLayer' }) => {
	const { mapObject, measurementLayer, dispatch } = useProject();

	useEffect(() => {
		if (!mapObject) return;

		let layer = getLayerByCustomId(mapObject, layerId);

		if (!layer) {
			layer = new VectorLayer({
				zIndex: 30,
				source: new VectorSource(),
				name: 'Measurements',
				properties: {
					customLayerId: layerId,
				},

				// Set style based on feature type
				style: feature => feature.get('style'),
			});
			mapObject.addLayer(layer);
		} else {
			layer.getSource().clear();
		}

		dispatch({ type: 'setMeasurementLayer', payload: layer });

		// Cleanup
		return () => {
			removeOverlaysWithElementClassName({
				mapObject: mapObject,
				className: `${layerId}Overlay`,
			});

			deleteLayerByCustomId(mapObject, layerId);
			dispatch({ type: 'setMeasurementLayer', payload: null });
		};
	}, [mapObject, layerId, dispatch]);

	if (!measurementLayer) return null;
	return (
		<li>
			<Checkbox
				id={layerId}
				label={measurementLayer.get('name')}
				layer={measurementLayer}
				defaultChecked={measurementLayer?.getVisible()}
				onCheck={() => {
					measurementLayer?.setVisible(true);
				}}
				onUncheck={() => {
					measurementLayer?.setVisible(false);
				}}
				disabled={false}
			/>
		</li>
	);
};

export default MeasurementLayer;
